import { authService } from '../pages/login/routes/Login'
import axios from 'axios'
import { loggedoutReasons } from '../constants/statusMessages'
import { useChosenCompany, useUser } from '../context/UserProvider'

const shouldNavigateToLogout = (errorResponse: any) => {
  if (![401, 403].includes(errorResponse?.response?.status)) {
    return false
  }

  return errorResponse?.request?.responseURL.includes('/api/flows')
}

export const axiosInstance = axios.create({
  timeout: 300000,
  baseURL: '/api',
  headers: {
    'cache-control': 'no-cache',
  },
})

const db = localStorage.getItem('db')

axiosInstance.interceptors.response.use(
  ({ data }) => data,
  (error) => {
    if (shouldNavigateToLogout(error)) {
      sessionStorage.setItem('loggedoutReason', loggedoutReasons.NORMAL_LOGOUT)
      window.location.replace('/logout')
    }
    return Promise.reject(error)
  },
)

axiosInstance.interceptors.request.use(async (config) => {
  const user = await authService.getUser()
  config.headers.authorization = `Bearer ${user.access_token}`
  return config
})

export const getArchivedDocuments = async (contactId: string, limit: string | number) => {
  try {
    const response = await axiosInstance.get(`/signed/documents?contactId=${contactId}&skip=0&limit=${limit}`)
    return response
  } catch (error) {
    throw error.response.data.Message || error.message
  }
}

export const getAttachmentById = async (attachmentId: string, mimeType: string, flowId: string) => {
  return axiosInstance
    .get(`/attachments/${attachmentId}?flowId=${flowId}`, {
      responseType: 'blob',
      headers: {
        'Content-Type': mimeType,
      },
    })
    .catch((error) => {
      throw error.response.data.Message || error.message
    })
}

export const fetchImprovedTableData = async ({
  fromDate,
  toDate,
  account,
  productType,
  companyId,
  contactId,
}: {
  fromDate: string
  toDate: string
  account: string
  productType: string
  companyId: string
  contactId?: string
}) => {
  const lastYearISO = new Date(new Date().setFullYear(new Date().getFullYear() - 5)).toISOString()
  const todayISO = new Date().toISOString()
  const url = `/fleet/${companyId}/accounts/${account}/expenses/${productType}?fromDate=${fromDate}&toDate=${toDate}&contactId=${contactId}`
  return axiosInstance
    .get(`${url}`, {
      params: {
        ...(db && { db }),
      },
    })
    .catch(console.error)
}

export const fetchTableData = async ({
  year,
  month,
  account,
  filter,
  companyId,
  fuelProducts,
}: {
  year: string | number
  month: string | number
  account: string
  filter: string
  companyId: string
  fuelProducts: string[]
  parkingProducts: string[]
}) => {
  /*   const searchString =
    year === 'ALL' ? '' : month === 'ALL' ? `/${year}` : `/${year}/${month}` */

  const url = `/fleet/${companyId}/accounts/${account}/expenses/${filter}`

  if (filter === 'parking') {
    const products = parkingProducts.join(',')
    return axiosInstance
      .get(`${url}?products=${products}`, {
        params: {
          ...(db && { db }),
        },
      })
      .catch(console.error)
  }
  if (filter === 'fuel') {
    const products = fuelProducts.join(',')
    return axiosInstance
      .get(`${url}?products=${products}`, {
        params: {
          ...(db && { db }),
        },
      })
      .catch(console.error)
  }
  return axiosInstance
    .get(url, {
      params: {
        ...(db && { db }),
      },
    })
    .catch(console.error)
}

export const getInsuranceInformation = async ({ accountNo, companyId, contactId }) => {
  return axiosInstance.get(`/fleet/${companyId}/accounts/${accountNo}/insurance?contactId=${contactId}`)
}

export const getAdditionalProducts = async ({
  accountNo,
  companyId,
  expenseId,
  contactId,
}: {
  accountNo: string
  companyId: string
  expenseId: string
  contactId: string
}) => {
  const encodeExpenseId = encodeURIComponent(expenseId)
  const fromFiveYearsAgo = new Date(new Date().setFullYear(new Date().getFullYear() - 5)).toISOString()

  const isDateableExpenseId = ['park', 'fuel', 'service', 'workshop', 'toll'].includes(expenseId)
  const todayISO = new Date().toISOString()
  return axiosInstance
    .get(
      `/fleet/${companyId}/accounts/${accountNo}/expenses/${encodeExpenseId}?contactId=${contactId}${
        isDateableExpenseId ? `&fromDate=${fromFiveYearsAgo}&toDate=${todayISO}` : ''
      }`,
      {
        params: {
          ...(db && { db }),
        },
      },
    )
    .catch(console.error)
}

export const getCarPark = async ({
  companyId,
  consolidationCode,
  filter,
  pageNo,
  pageSize,
}: {
  companyId: string
  consolidationCode?: string
  filter?: string
  pageNo?: number
  pageSize?: number
}) => {
  return axiosInstance
    .get(
      `/fleet/simple?companyId=${companyId}${
        consolidationCode === '_ALL_' ? '' : `&consolidatedInvoice=${consolidationCode}`
      }`,
      {
        params: {
          ...(db && { db }),
          ...(filter && { filter }),
          ...(pageNo && pageSize && { pageNo, pageSize }),
        },
      },
    )
    .catch(console.error)
}

export const getFleetSummary = async ({
  companyId,
  consolidationCode,
}: {
  companyId: string
  consolidationCode?: string
}) => {
  return axiosInstance.get(
    `/fleet/summary?companyId=${companyId}${
      consolidationCode === '_ALL_' ? '' : `&consolidatedInvoice=${consolidationCode}`
    }`,
    {
      params: {
        ...(db && { db }),
      },
    },
  )
}

export const getCarImage = async ({ budgetNo, companyId }: { budgetNo: string; companyId: string }) => {
  return axiosInstance
    .get(`/fleet/${companyId}/budgets/${budgetNo}/pictures`, {
      params: {
        ...(db && { db }),
      },
    })
    .catch(console.error)
}

export const fetchLatestServiceInvoice = async (companyId: string, accountId: string, contactId: string) => {
  return axiosInstance.get(`/fleet/${companyId}/accounts/${accountId}/expenses/service`, {
    params: {
      contactId,
      findFirst: 1,
    },
  })
}

export const sendMessageEmail = async (payload: {
  company: string
  registrationNumber?: string
  name: string
  mail: string
  phone: string
  address?: string
  postCode?: string
  city?: string
  message: string
}) => axiosInstance.post(`/contact-us`, payload)

export const recordMileage = async (accountNumber: string, mileage: number) => {
  return axiosInstance.post('/mileage', { AccNumber: `${accountNumber}`, Mileage: `${mileage}` }).catch((err) => {
    throw err.response.data.Message || err.message
  })
}
